import React, { useState, useEffect } from 'react'
import { IconButton, Tooltip, CircularProgress } from '@mui/material'
import { Check, CheckBoxOutlineBlank } from '@mui/icons-material'
import { FeedbackGridApi } from '../../api/feedbackGridApi'

interface CheckedActionProps {
	feedbackId: number
	initialValue: boolean
	api: FeedbackGridApi
	onUpdate?: (newValue: boolean) => void
	translations: Record<string, string>
}

export const CheckedAction: React.FC<CheckedActionProps> = ({
	feedbackId,
	initialValue,
	api,
	onUpdate,
	translations = {},
}) => {
	const [value, setValue] = useState(initialValue)
	const [loading, setLoading] = useState(false)

	// Sync state with prop changes (e.g. when categories are edited and row data is updated)
	useEffect(() => {
		setValue(initialValue)
	}, [initialValue])

	const handleToggle = async (e: React.MouseEvent) => {
		e.stopPropagation() // Prevent row click

		setLoading(true)
		const newValue = !value

		try {
			const result = await api.updateCell(feedbackId, 'checked', newValue)

			if (result.success) {
				setValue(newValue)
				onUpdate?.(newValue)
			} else {
				console.error('Failed to update checked status:', result.error)
			}
		} catch (error) {
			console.error('Error updating checked status:', error)
		} finally {
			setLoading(false)
		}
	}

	if (loading) {
		return <CircularProgress size={20} />
	}

	const tooltipTitle = value
		? translations.markAsUnchecked || 'Mark as unchecked'
		: translations.markAsChecked || 'Mark as checked'

	return (
		<Tooltip title={tooltipTitle}>
			<IconButton onClick={handleToggle} size="small" color={value ? 'success' : 'default'}>
				{value ? <Check /> : <CheckBoxOutlineBlank />}
			</IconButton>
		</Tooltip>
	)
}
